import { useState } from 'react';
import {
  Modal, Upload, Button, Space, Typography, Table, Alert, message,
} from 'antd';
import {
  InboxOutlined, DownloadOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { registrationApi } from '../../../api/registration.api';
import * as XLSX from 'xlsx';

const { Dragger } = Upload;
const { Text, Title } = Typography;

const SUMMARY_CARDS = [
  { key: 'total',           label: 'Total Rows',         bg: '#f0f5ff', color: '#1d39c4' },
  { key: 'newChildren',     label: 'New Children',       bg: '#f6ffed', color: '#389e0d' },
  { key: 'existingChildren',label: 'Existing Children',  bg: '#e6fffb', color: '#08979c' },
  { key: 'registered',      label: 'Registered',         bg: '#f9f0ff', color: '#722ed1' },
  { key: 'alreadyRegistered',label: 'Already Registered',bg: '#fff7e6', color: '#d46b08' },
  { key: 'failed',          label: 'Failed',             bg: '#fff2f0', color: '#cf1322' },
];

const errorColumns = [
  { title: 'Row #',        dataIndex: 'row',     key: 'row',     width: 80 },
  { title: 'Error Detail', dataIndex: 'message', key: 'message' },
];

const EventImportExcelModal = ({ open, onClose, eventId }) => {
  const [file,    setFile]    = useState(null);
  const [results, setResults] = useState(null);
  const queryClient = useQueryClient();

  const importMutation = useMutation({
    mutationFn: (formData) => registrationApi.bulkImport(formData),
    onSuccess: (res) => {
      const data = res.data;
      setResults(data);
      queryClient.invalidateQueries(['event-registrations', eventId]);
      queryClient.invalidateQueries(['event', eventId]);
      queryClient.invalidateQueries(['children']);
      if (data.registered > 0) {
        message.success(
          `${data.registered} child${data.registered !== 1 ? 'ren' : ''} registered` +
          (data.newChildren > 0 ? ` (${data.newChildren} new profile${data.newChildren !== 1 ? 's' : ''} created)` : '')
        );
      }
    },
    onError: (err) => message.error(err.message || 'Import failed'),
  });

  const handleUpload = () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    importMutation.mutate(formData);
  };

  const downloadSample = () => {
    const sampleRows = [
      {
        'Child Name':     'Rahul Sharma',
        'Gender':         'male',
        'Date of Birth(YYYY-MM-DD)':  '2018-05-15',
        'Standard':       '5th',
        'Section':        'A',
        'Hobbies':        'Drawing, Cricket',
        'Favourite Food': 'Rice, Mango',
        'Parent Name':    'Amit Sharma',
        'Parent Phone':   '9876543210',
        'Parent Email':   'amit.sharma@email.com',
        'Parent Address': '12, MG Road, Hyderabad',
      },
      {
        'Child Name':     'Priya Reddy',
        'Gender':         'female',
        'Date of Birth(YYYY-MM-DD)':  '2019-11-20',
        'Standard':       '3rd',
        'Section':        'B',
        'Hobbies':        'Painting, Reading',
        'Favourite Food': 'Idli, Dosa',
        'Parent Name':    'Sunita Reddy',
        'Parent Phone':   '9123456780',
        'Parent Email':   'sunita.reddy@email.com',
        'Parent Address': '45, Jubilee Hills, Hyderabad',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleRows);
    ws['!cols'] = [
      { wch: 20 }, { wch: 10 }, { wch: 16 }, { wch: 10 },
      { wch: 10 }, { wch: 28 }, { wch: 22 }, { wch: 22 },
      { wch: 20 }, { wch: 15 }, { wch: 28 }, { wch: 30 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Children');
    XLSX.writeFile(wb, 'event_children_import_template.xlsx');
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    onClose();
  };

  const summaryValues = results
    ? {
        total:            results.total            ?? 0,
        newChildren:      results.newChildren      ?? 0,
        existingChildren: results.existingChildren ?? 0,
        registered:       results.registered       ?? 0,
        alreadyRegistered:results.alreadyRegistered?? 0,
        failed:           results.errors?.length   ?? 0,
      }
    : null;

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: 'var(--wl-primary)' }} />
          <span>Import Children via Excel</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      {!results ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Each row creates a child profile (if new) and registers the child for this event."
            action={
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={downloadSample}
                style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)' }}
              >
                Sample Template
              </Button>
            }
          />

          <Dragger
            beforeUpload={(f) => { setFile(f); return false; }}
            onRemove={() => setFile(null)}
            maxCount={1}
            accept=".xlsx,.xls"
            style={{ borderColor: 'var(--wl-text-muted)' }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: 'var(--wl-primary)' }} />
            </p>
            <p className="ant-upload-text">Click or drag an Excel file here to upload</p>
            <p className="ant-upload-hint">Supports .xlsx and .xls — max 5 MB</p>
          </Dragger>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!file}
              loading={importMutation.isPending}
              onClick={handleUpload}
              style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
            >
              Import & Register
            </Button>
          </div>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Summary cards — 3 per row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {SUMMARY_CARDS.map(({ key, label, bg, color }) => (
              <div
                key={key}
                style={{
                  flex: '1 1 calc(33% - 10px)',
                  minWidth: 120,
                  textAlign: 'center',
                  background: bg,
                  borderRadius: 10,
                  padding: '12px 8px',
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color }}>
                  {summaryValues[key]}
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
              </div>
            ))}
          </div>

          {results.errors?.length > 0 && (
            <div>
              <Title level={5} style={{ marginBottom: 8 }}>
                Error Details ({results.errors.length})
              </Title>
              <Table
                columns={errorColumns}
                dataSource={results.errors.map((e, i) => ({ ...e, key: i }))}
                size="small"
                pagination={{ pageSize: 5, size: 'small' }}
                scroll={{ y: 200 }}
              />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setFile(null); setResults(null); }}>
              Import More
            </Button>
            <Button
              type="primary"
              onClick={handleClose}
              style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
            >
              Done
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default EventImportExcelModal;
